import React from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const HERO_BG = "https://images.unsplash.com/photo-1634638021403-70f46d19fc02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXAlMjBjb250YWluZXJzJTIwc2hpcHBpbmclMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzc3Njg2MzA4fDA&ixlib=rb-4.1.0&q=85";

const HERO_STATS = [
  { value: 'Bali', label: 'Origin' },
  { value: 'Handwoven', label: 'By Artisans' },
  { value: 'B2B', label: 'Export Ready' },
  { value: 'Pilot', label: 'Orders Welcome' },
];

export default function HeroSection() {
  const { t } = useLanguage();

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Global shipping logistics"
          fetchpriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0f3b2e]/75" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full">
        <div className="max-w-3xl">
          <p className="text-brand-orange font-semibold text-sm uppercase tracking-[0.2em] mb-6">
            {t('hero.subtitle')}
          </p>

          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.1] mb-8"
            data-testid="hero-headline"
          >
            {t('hero.headline')}
          </h1>

          <p className="text-lg text-white/75 leading-relaxed mb-12 max-w-2xl">
            {t('hero.description')}
          </p>

          <div className="flex flex-wrap gap-4">
            <ScrollLink
              to="quote"
              smooth={true}
              duration={500}
              offset={-80}
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-8 py-4 font-bold text-sm uppercase tracking-wider cursor-pointer hover:bg-brand-orange-hover transition-all shadow-lg hover:shadow-xl group"
              data-testid="hero-cta-button"
            >
              {t('hero.cta')}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </ScrollLink>

            <ScrollLink
              to="products"
              smooth={true}
              duration={500}
              offset={-80}
              className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-8 py-4 font-bold text-sm uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-all"
              data-testid="hero-secondary-button"
            >
              {t('hero.ctaSecondary')}
            </ScrollLink>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 border-t border-white/15 pt-10">
          {HERO_STATS.map((stat, i) => (
            <div key={stat.value} data-testid={`hero-stat-${i}`}>
              <p className="text-3xl md:text-4xl font-black text-white tracking-tight">{stat.value}</p>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-1 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <ScrollLink
          to="products"
          smooth={true}
          duration={500}
          offset={-80}
          className="cursor-pointer"
        >
          <ChevronDown className="w-6 h-6 text-white/40 animate-bounce" />
        </ScrollLink>
      </div>
    </section>
  );
}
